/**
 * Shared city-branded header + portal nav for citizen advisor shells and Zoning.
 * Visual tokens match InterviewFlow landing (Fraunces / Source Sans 3 / #1976d2).
 */
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Link, Stack, Typography, useMediaQuery,
} from '@mui/material';
import DemoSiteNav from '../demo/DemoSiteNav';
import {
  IF_BRAND,
  ensureInterviewFlowFonts,
  interviewFlowCssVars,
} from '../../theme/interviewFlowBrand';

export default function CitizenProductChrome({
  cityName = 'City and County of San Francisco',
  title = null,
  subtitle = null,
  portalLinks = [],
  primaryColor = IF_BRAND.accent,
  banner = null,
  endAdornment = null,
  children = null,
  testId = 'citizen-product-chrome',
}) {
  const { t } = useTranslation();
  const isCompact = useMediaQuery('(max-height:820px), (max-width:1100px)');

  useEffect(() => { ensureInterviewFlowFonts(); }, []);

  return (
    <Box
      data-testid={testId}
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: IF_BRAND.canvas,
        fontFamily: IF_BRAND.body,
        ...interviewFlowCssVars(primaryColor),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: isCompact ? 1.25 : 1.75,
            pb: isCompact ? 1 : 1.25,
            background: IF_BRAND.headerGradient,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
          >
            <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
              <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: IF_BRAND.display,
                    fontWeight: 700,
                    fontSize: isCompact
                      ? { xs: '1.15rem', sm: '1.35rem' }
                      : { xs: '1.35rem', sm: '1.6rem' },
                    letterSpacing: '-0.03em',
                    lineHeight: 1.15,
                    color: 'var(--citizen-primary, #1976d2)',
                  }}
                >
                  {cityName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {t('citizen.guidanceOnly')}
                  {' · '}
                  <Link component={RouterLink} to="/help/permits" underline="hover" color="inherit">
                    {t('citizen.howThisWorks')}
                  </Link>
                </Typography>
              </Stack>
              {!isCompact && (title || subtitle) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.4, maxWidth: 560, lineHeight: 1.35 }}
                >
                  {title ? (
                    <>
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {title}
                      </Box>
                      {subtitle ? ` — ${subtitle}` : null}
                    </>
                  ) : subtitle}
                </Typography>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flexShrink: 0, maxWidth: '100%' }}
            >
              <DemoSiteNav />
              {endAdornment}
              {portalLinks.map((p) => (
                <Button
                  key={p.to}
                  component={RouterLink}
                  to={p.to}
                  size="small"
                  variant={p.active ? 'contained' : 'outlined'}
                  sx={{
                    textTransform: 'none',
                    minHeight: 30,
                    px: 1.1,
                    borderColor: 'divider',
                    fontFamily: IF_BRAND.body,
                    ...(p.active ? {
                      bgcolor: 'var(--citizen-primary, #1976d2)',
                      borderColor: 'var(--citizen-primary, #1976d2)',
                      '&:hover': {
                        bgcolor: 'var(--citizen-primary, #1976d2)',
                        filter: 'brightness(0.92)',
                      },
                    } : {
                      color: 'text.secondary',
                    }),
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Box>
        {banner && (
          <Alert
            severity="info"
            sx={{
              borderRadius: 0,
              py: 0.35,
              '& .MuiAlert-message': { py: 0.25, fontSize: '0.8125rem' },
            }}
          >
            {banner}
          </Alert>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    </Box>
  );
}

/** Shared SF portal strip used by Zoning + permit shells. */
export function buildSfCitizenPortalLinks({
  active = 'zoning',
  cityQs = '',
  includeZoning = true,
} = {}) {
  const base = '/v2/demo/permit';
  const path = (id) => `${id ? `${base}/${id}` : base}${cityQs}`;
  const links = [
    { to: path(null), label: 'Advisor', active: active === 'advisor' },
    { to: path('business'), label: 'Business / commercial', active: active === 'business' },
    { to: path('residential'), label: 'Residential', active: active === 'residential' },
    { to: path('events'), label: 'Special events', active: active === 'events' },
  ];
  if (includeZoning) {
    links.push({
      to: cityQs ? `/zoning-check${cityQs}` : '/zoning-check',
      label: 'Zoning',
      active: active === 'zoning',
    });
  }
  return links;
}

export function pickAdvisorPortalForUse(useLabel = '') {
  const u = String(useLabel || '').toLowerCase();
  if (/\b(event|block\s*party|fair|parade|festival)\b/.test(u)) return 'events';
  if (/\b(adu|in-?law|residential|house|remodel|renovat|solar|home\s+occupation|home\s+bakery|deck|shed)\b/.test(u)) {
    return 'residential';
  }
  if (/\b(restaurant|cafe|retail|office|store|shop|commercial|yoga|gym|bar|bakery)\b/.test(u)) {
    return 'business';
  }
  return 'business';
}
